import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/Button';
import { AdminConfigDrawer } from '../components/AdminConfigDrawer';
import { useAuth, isAdminRole } from '../contexts/AuthContext';
import { analyticsApi } from '../lib/api';
import { Settings, Download, ChevronUp, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface EditorRow {
  editorId: string;
  editorName: string;
  creativeCount: number;
  spend: number;
  revenue: number | null;
  profit: number | null;
  roas: number | null;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number | null;
  hookRate: number | null;
  holdRate: number | null;
}

interface Stats {
  totalSpend: number;
  totalRevenue: number | null;
  totalProfit: number | null;
  totalRoas: number | null;
  totalConversions: number;
  coverage: number;
  matchedAds: number;
  totalAds: number;
  editorCount: number;
}

interface CoverageMetrics {
  byMethod: { ucode: number; regex: number; code: number; token: number; fuzzy: number; [key: string]: number };
  total: number;
  unmatched: number;
}

interface CreativeRow {
  creativeId: string;
  filename: string;
  adCount: number;
  spend: number;
  revenue: number | null;
  profit: number | null;
  roas: number | null;
}

type SortKey = 'editorName' | 'creativeCount' | 'spend' | 'revenue' | 'profit' | 'roas' | 'conversions';
type SortDir = 'asc' | 'desc';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt$(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return '$' + Math.round(n).toLocaleString();
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toFixed(1) + '%';
}

function roasClass(roas: number | null): string {
  if (roas === null) return 'text-muted-foreground';
  if (roas >= 1.0) return 'text-green-600 dark:text-green-400 font-semibold';
  return 'text-red-600 dark:text-red-400 font-semibold';
}

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

function defaultDateFrom() {
  return '2026-04-24';
}

function defaultDateTo() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card rounded-lg border border-border px-5 py-4">
      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-semibold text-foreground">{value}</p>
    </div>
  );
}

// ── Sort button ───────────────────────────────────────────────────────────────

function SortBtn({ col, active, dir, onClick }: { col: string; active: boolean; dir: SortDir; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
      {col}
      {active
        ? dir === 'desc' ? <ChevronDown size={13} /> : <ChevronUp size={13} />
        : <ChevronDown size={13} className="opacity-30" />}
    </button>
  );
}

// ── Cache helpers ─────────────────────────────────────────────────────────────

const CACHE_KEY = 'analytics_engine_cache_v1';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

function readCache(): { lastPipelineRun: string; fetchedAt: number; dateFrom: string; dateTo: string; editorId: string; data: any } | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCache(lastPipelineRun: string, dateFrom: string, dateTo: string, editorId: string, data: any) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ lastPipelineRun, fetchedAt: Date.now(), dateFrom, dateTo, editorId, data }));
  } catch { /* storage full - ignore */ }
}

function cacheIsValid(cache: ReturnType<typeof readCache>, dateFrom: string, dateTo: string, editorId: string): boolean {
  if (!cache) return false;
  if (Date.now() - cache.fetchedAt > CACHE_TTL_MS) return false;
  if (cache.dateFrom !== dateFrom || cache.dateTo !== dateTo || cache.editorId !== editorId) return false;
  return true;
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AnalyticsEnginePage() {
  const { user } = useAuth();
  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const isAdmin = isAdminRole(user?.role, additionalRoles);
  const isCreative = user?.role === 'creative';
  const canRunPipeline = isAdmin ||
    additionalRoles.includes('creative_head') || user?.role === 'creative_head' ||
    additionalRoles.includes('ceo') || user?.role === 'ceo';

  // Filters
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo]   = useState(defaultDateTo);
  const [editorId, setEditorId] = useState('');
  const [matchMethods, setMatchMethods] = useState({ ucode: true, regex: true, token: true, fuzzy: false });

  // Data
  const [stats, setStats]                 = useState<Stats | null>(null);
  const [editorPerf, setEditorPerf]       = useState<EditorRow[]>([]);
  const [coverageMetrics, setCoverage]    = useState<CoverageMetrics | null>(null);
  const [configState, setConfigState]     = useState<any[]>([]);
  const [lastSyncMin, setLastSyncMin]       = useState<number | null>(null);
  const [lastPipelineRun, setLastPipelineRun] = useState<string | null>(null);
  const [pipelineDataFrom, setPipelineDataFrom] = useState<string | null>(null);
  const [pipelineDataTo,   setPipelineDataTo]   = useState<string | null>(null);

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [drawerOpen, setDrawer] = useState(false);

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Accordion: expanded editor rows + their creative data
  const [expandedEditors, setExpandedEditors] = useState<Set<string>>(new Set());
  const [editorCreatives, setEditorCreatives] = useState<Record<string, CreativeRow[]>>({});
  const [loadingCreatives, setLoadingCreatives] = useState<Set<string>>(new Set());

  const toggleEditorRow = useCallback(async (editorId: string) => {
    setExpandedEditors(prev => {
      const next = new Set(prev);
      if (next.has(editorId)) { next.delete(editorId); return next; }
      next.add(editorId);
      return next;
    });
    // Lazy-load creatives if not yet fetched
    if (!editorCreatives[editorId]) {
      setLoadingCreatives(prev => new Set(prev).add(editorId));
      try {
        const res = await analyticsApi.getEditorCreatives({ editorId, dateFrom, dateTo });
        const rows: CreativeRow[] = res.data?.data || [];
        setEditorCreatives(prev => ({ ...prev, [editorId]: rows }));
      } catch { /* ignore */ } finally {
        setLoadingCreatives(prev => { const s = new Set(prev); s.delete(editorId); return s; });
      }
    }
  }, [editorCreatives, dateFrom, dateTo]);

  const applyEngineData = useCallback((d: any) => {
    setStats(d.stats);
    setEditorPerf(d.editorPerformance || []);
    setCoverage(d.coverageMetrics);
    setConfigState(d.configState || []);
    setLastSyncMin(0);
    if (d.lastPipelineRun) setLastPipelineRun(d.lastPipelineRun);
    if (d.pipelineDataFrom) setPipelineDataFrom(d.pipelineDataFrom.split('T')[0]);
    if (d.pipelineDataTo)   setPipelineDataTo(d.pipelineDataTo.split('T')[0]);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await analyticsApi.getEngineData({
        dateFrom,
        dateTo,
        ...(editorId ? { editorId } : {})
      });
      const d = res.data?.data;
      if (d) {
        applyEngineData(d);
        if (d.lastPipelineRun) writeCache(d.lastPipelineRun, dateFrom, dateTo, editorId, d);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, editorId, applyEngineData]);

  // Auto-fetch when filters change (debounced 600ms) — skipped if cache is still fresh for these filters
  const filterDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) return; // skip on initial render — mount effect handles that
    if (filterDebounce.current) clearTimeout(filterDebounce.current);
    filterDebounce.current = setTimeout(() => {
      const cache = readCache();
      if (cacheIsValid(cache, dateFrom, dateTo, editorId)) {
        applyEngineData(cache!.data);
      } else {
        fetchData();
      }
    }, 600);
    return () => { if (filterDebounce.current) clearTimeout(filterDebounce.current); };
  }, [dateFrom, dateTo, editorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: show cached data instantly; only re-fetch if cache stale (>6h) or pipeline ran
  useEffect(() => {
    mountedRef.current = true;
    const cache = readCache();
    if (cache?.data) applyEngineData(cache.data);

    analyticsApi.getAnalyticsPipelineStatus().then(res => {
      const d = res.data?.data;
      const serverRun: string | null = d?.lastPipelineRun || null;
      if (d?.pipelineDataFrom) setPipelineDataFrom((d.pipelineDataFrom as string).split('T')[0]);
      if (d?.pipelineDataTo)   setPipelineDataTo((d.pipelineDataTo as string).split('T')[0]);
      const pipelineChanged = serverRun && serverRun !== cache?.lastPipelineRun;
      const cacheStale = !cacheIsValid(cache, dateFrom, dateTo, editorId);
      if (pipelineChanged || cacheStale) fetchData();
    }).catch(() => {
      if (!cache?.data) fetchData();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Background poll: check for new pipeline runs every 30s and auto-refresh if changed
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await analyticsApi.getAnalyticsPipelineStatus();
        const serverRun: string | null = res.data?.data?.lastPipelineRun || null;
        const cache = readCache();
        if (serverRun && serverRun !== lastPipelineRun && serverRun !== cache?.lastPipelineRun) {
          fetchData();
        }
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [lastPipelineRun, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Export CSV
  const exportCSV = () => {
    const headers = ['Editor', 'Creatives', 'Spend', 'Revenue', 'Profit', 'ROAS', 'Impressions', 'Clicks', 'Conversions', 'CTR', 'Hook Rate', 'Hold Rate'];
    const rows = editorPerf.map(r => [
      r.editorName,
      r.creativeCount,
      r.spend.toFixed(2),
      r.revenue     !== null ? r.revenue.toFixed(2)     : '',
      r.profit      !== null ? r.profit.toFixed(2)      : '',
      r.roas        !== null ? r.roas.toFixed(4)        : '',
      r.impressions,
      r.clicks,
      r.conversions,
      r.ctr         !== null ? r.ctr.toFixed(2)         : '',
      r.hookRate    !== null ? r.hookRate.toFixed(2)    : '',
      r.holdRate    !== null ? r.holdRate.toFixed(2)    : '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `analytics-engine-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Sorted + paginated rows
  const sorted = [...editorPerf].sort((a, b) => {
    let av: any = a[sortKey];
    let bv: any = b[sortKey];
    if (av === null) av = sortDir === 'desc' ? -Infinity : Infinity;
    if (bv === null) bv = sortDir === 'desc' ? -Infinity : Infinity;
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'desc' ? bv - av : av - bv;
  });
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows   = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
    setPage(1);
  };

  return (
    <DashboardLayout
      title="Analytics Engine"
      actions={
        <div className="flex gap-2">
          {canRunPipeline && (
            <Button variant="outline" size="sm" onClick={() => setDrawer(true)} className="flex items-center gap-1.5">
              <Settings size={14} />
              Admin Config
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportCSV} className="flex items-center gap-1.5">
            <Download size={14} />
            Export CSV
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6 p-6">

        {/* ── KPI strip ── */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
            <KpiCard label="Total Spend"     value={fmt$(stats.totalSpend)} />
            <KpiCard label="Total Revenue"   value={fmt$(stats.totalRevenue)} />
            <KpiCard label="Total Profit"    value={fmt$(stats.totalProfit)} />
            <KpiCard label="ROAS"            value={stats.totalRoas !== null ? stats.totalRoas.toFixed(2) + 'x' : '—'} />
            <KpiCard label="Conversions"     value={fmtNum(stats.totalConversions)} />
            <KpiCard label="Coverage"        value={fmtPct(stats.coverage)} />
            <KpiCard label="Active Editors"  value={stats.editorCount.toLocaleString()} />
          </div>
        )}

        {/* ── Filter bar ── */}
        <div className="bg-card border border-border rounded-lg px-5 py-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Date range */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); }}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Editor filter — hidden for creative role (they only see their own data) */}
            {!isCreative && (
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Editor ID</label>
                <input
                  type="text"
                  placeholder="Filter by editor..."
                  value={editorId}
                  onChange={e => { setEditorId(e.target.value); setPage(1); }}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            {/* Match method checkboxes */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Match Method</label>
              <div className="flex gap-3 items-center">
                {(['ucode', 'regex', 'token', 'fuzzy'] as const).map(m => (
                  <label key={m} className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={matchMethods[m]}
                      onChange={e => setMatchMethods(prev => ({ ...prev, [m]: e.target.checked }))}
                      className="h-3.5 w-3.5"
                    />
                    <span className="capitalize">{m.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>

            {loading && (
              <span className="self-end text-xs text-muted-foreground animate-pulse pb-2">Loading…</span>
            )}
          </div>
        </div>

        {/* ── Coverage by method ── */}
        {coverageMetrics && (
          <div className="bg-card border border-border rounded-lg px-5 py-4">
            <h2 className="text-sm font-semibold text-foreground mb-3">Coverage by Match Method</h2>
            <div className="flex flex-wrap gap-6 text-sm">
              {(Object.entries(coverageMetrics.byMethod) as [string, number][]).map(([method, count]) => (
                <div key={method} className="flex flex-col">
                  <span className="text-xs text-muted-foreground uppercase">{method}</span>
                  <span className="font-semibold text-foreground">{count.toLocaleString()}</span>
                </div>
              ))}
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground uppercase">Unmatched</span>
                <span className="font-semibold text-red-500">{coverageMetrics.unmatched.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* ── Editor performance table ── */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">
              Editor Performance
              {editorPerf.length > 0 && <span className="text-muted-foreground font-normal ml-2">({editorPerf.length})</span>}
            </h2>
          </div>

          {loading && editorPerf.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              Loading...
            </div>
          ) : editorPerf.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              No data for selected filters. Sync may be required.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left px-4 py-3">
                        <SortBtn col="Editor" active={sortKey === 'editorName'} dir={sortDir} onClick={() => handleSort('editorName')} />
                      </th>
                      <th className="text-right px-4 py-3">
                        <SortBtn col="Creatives" active={sortKey === 'creativeCount'} dir={sortDir} onClick={() => handleSort('creativeCount')} />
                      </th>
                      <th className="text-right px-4 py-3">
                        <SortBtn col="Spend" active={sortKey === 'spend'} dir={sortDir} onClick={() => handleSort('spend')} />
                      </th>
                      <th className="text-right px-4 py-3">
                        <SortBtn col="Revenue" active={sortKey === 'revenue'} dir={sortDir} onClick={() => handleSort('revenue')} />
                      </th>
                      <th className="text-right px-4 py-3">
                        <SortBtn col="Profit" active={sortKey === 'profit'} dir={sortDir} onClick={() => handleSort('profit')} />
                      </th>
                      <th className="text-right px-4 py-3">
                        <SortBtn col="ROAS" active={sortKey === 'roas'} dir={sortDir} onClick={() => handleSort('roas')} />
                      </th>
                      <th className="text-right px-4 py-3">
                        <SortBtn col="Conversions" active={sortKey === 'conversions'} dir={sortDir} onClick={() => handleSort('conversions')} />
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">CTR</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hook Rate</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hold Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {pageRows.map(row => {
                      const isExpanded = expandedEditors.has(row.editorId);
                      const isLoadingC = loadingCreatives.has(row.editorId);
                      const creatives  = editorCreatives[row.editorId] || [];
                      return (
                        <React.Fragment key={row.editorId}>
                          <tr
                            className="hover:bg-muted/30 transition-colors cursor-pointer"
                            onClick={() => toggleEditorRow(row.editorId)}
                          >
                            <td className="px-4 py-3 font-medium text-foreground">
                              <span className="flex items-center gap-1.5">
                                <ChevronRight
                                  size={13}
                                  className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                />
                                {row.editorName}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-muted-foreground">{row.creativeCount.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right">{fmt$(row.spend)}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">{fmt$(row.revenue)}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">{fmt$(row.profit)}</td>
                            <td className={`px-4 py-3 text-right ${roasClass(row.roas)}`}>
                              {row.roas !== null ? row.roas.toFixed(2) + 'x' : '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-muted-foreground">{fmtNum(row.conversions || null)}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">
                              {row.ctr !== null ? row.ctr.toFixed(2) + '%' : '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-muted-foreground">
                              {row.hookRate !== null ? row.hookRate.toFixed(2) + '%' : '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-muted-foreground">
                              {row.holdRate !== null ? row.holdRate.toFixed(2) + '%' : '—'}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={10} className="px-0 py-0 bg-muted/20">
                                <div className="px-8 py-3">
                                  {isLoadingC ? (
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                                      <Loader2 size={12} className="animate-spin" /> Loading creatives…
                                    </div>
                                  ) : creatives.length === 0 ? (
                                    <p className="text-xs text-muted-foreground py-2">No creatives found for this editor in the selected date range.</p>
                                  ) : (
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-muted-foreground uppercase tracking-wide">
                                          <th className="text-left pb-1.5 font-medium">Creative File</th>
                                          <th className="text-right pb-1.5 font-medium">Ads</th>
                                          <th className="text-right pb-1.5 font-medium">Spend</th>
                                          <th className="text-right pb-1.5 font-medium">Revenue</th>
                                          <th className="text-right pb-1.5 font-medium">Profit</th>
                                          <th className="text-right pb-1.5 font-medium">ROAS</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-border/50">
                                        {creatives.map(c => (
                                          <tr key={c.creativeId} className="hover:bg-muted/20">
                                            <td className="py-1.5 text-foreground font-mono truncate max-w-xs">{c.filename}</td>
                                            <td className="py-1.5 text-right text-muted-foreground">{c.adCount}</td>
                                            <td className="py-1.5 text-right">{fmt$(c.spend)}</td>
                                            <td className="py-1.5 text-right text-muted-foreground">{fmt$(c.revenue)}</td>
                                            <td className="py-1.5 text-right text-muted-foreground">{fmt$(c.profit)}</td>
                                            <td className={`py-1.5 text-right ${roasClass(c.roas)}`}>
                                              {c.roas !== null ? c.roas.toFixed(2) + 'x' : '—'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Admin Config Drawer */}
      {canRunPipeline && (
        <AdminConfigDrawer
          open={drawerOpen}
          onClose={() => setDrawer(false)}
          configState={configState}
          onConfigSaved={fetchData}
          onPipelineComplete={() => {
            localStorage.removeItem(CACHE_KEY);
            fetchData();
          }}
        />
      )}
    </DashboardLayout>
  );
}
